import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {
    Box,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import {Timestamp} from "firebase/firestore";
import {getDownloadURL, ref, uploadBytesResumable} from "firebase/storage";
import firebaseService from "../../../services/firebaseService";
import dateCardTemplateService from "../../../services/dateCardTemplateService";
import {IDateCardTemplate, TemplateGender} from "../../../types/DateCardTemplate";

const THUMB_SCALE = 0.5;
const THUMB_QUALITY = 0.5;
const THUMB_BASE64_QUALITY = THUMB_QUALITY / 3;

const MAX_THUMB_BYTES = 800_000;

function compressToBase64(img: HTMLImageElement, scale: number, quality: number): string {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', quality);
}

function compressToBlob(img: HTMLImageElement, scale: number, quality: number): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
            'image/jpeg',
            quality,
        );
    });
}

function compressWithFallback(img: HTMLImageElement): string {
    let scale = THUMB_SCALE;
    let quality = THUMB_BASE64_QUALITY;

    let thumb = compressToBase64(img, scale, quality);

    // Size ≈ scale² × quality × C  →  reducing both by f shrinks by f³.
    // Solve for f directly so one pass always lands under the limit.
    while (thumb.length > MAX_THUMB_BYTES) {
        const f = Math.cbrt((MAX_THUMB_BYTES * 0.9) / thumb.length);
        scale = Math.max(scale * f, 0.05);
        quality = Math.max(quality * f, 0.1);
        thumb = compressToBase64(img, scale, quality);
    }

    return thumb;
}

const OTHER_VALUE = '__other__';

export const DateCardDesignAdd: React.FC = () => {
    const navigate = useNavigate();
    const [existingCategories, setExistingCategories] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [name, setName] = useState('');
    const [genderMen, setGenderMen] = useState(true);
    const [genderWomen, setGenderWomen] = useState(false);
    const [file, setFile] = useState<File>();
    const [preview, setPreview] = useState<string | undefined>();
    const [thumb, setThumb] = useState<string | undefined>();
    const [thumbBlob, setThumbBlob] = useState<Blob | undefined>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        dateCardTemplateService.getCategories().then(setExistingCategories);
    }, []);

    useEffect(() => {
        if (!file) {
            setPreview(undefined);
            setThumb(undefined);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreview(url);
        setThumb(undefined);
        setThumbBlob(undefined);

        const img = new Image();
        img.onload = async () => {
            setThumb(compressWithFallback(img));
            const blob = await compressToBlob(img, THUMB_SCALE, THUMB_QUALITY);
            setThumbBlob(blob);
        };
        img.src = url;

        return () => URL.revokeObjectURL(url);
    }, [file]);

    function uploadThumb(): Promise<string> {
        if (!thumbBlob) return Promise.resolve('');

        return new Promise((resolve, reject) => {
            const storageRef = ref(
                firebaseService.storage,
                `/DateCardTemplateImages/thumbs/${Timestamp.now().seconds}.jpg`,
            );
            const uploadTask = uploadBytesResumable(storageRef, thumbBlob);

            uploadTask.on(
                'state_changed',
                () => {},
                (err) => reject(err),
                async () => {
                    const thumbUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(thumbUrl);
                },
            );
        });
    }

    function uploadFile(): Promise<string> {
        if (!file) return Promise.resolve('');

        return new Promise((resolve, reject) => {
            const splitFile = file.name.split('.');
            const fileEnding = splitFile.length > 1 ? splitFile[splitFile.length - 1] : '';
            const storageRef = ref(
                firebaseService.storage,
                `/DateCardTemplateImages/${Timestamp.now().seconds}.${fileEnding}`
            );
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                () => {},
                (err) => reject(err),
                async () => {
                    const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(imageUrl);
                }
            );
        });
    }

    async function save() {
        const category = selectedCategory === OTHER_VALUE ? newCategory.trim() : selectedCategory;
        const genders: TemplateGender[] = [];
        if (genderMen) genders.push('men');
        if (genderWomen) genders.push('women');
        if (!category || !name.trim() || genders.length === 0 || !thumb) return;

        setSaving(true);
        try {
            const [image, thumbURL] = await Promise.all([uploadFile(), uploadThumb()]);
            const isNewCategory = selectedCategory === OTHER_VALUE;
            for (let i = 0; i < genders.length; i++) {
                const template: IDateCardTemplate = {
                    category,
                    name: name.trim(),
                    image,
                    thumb,
                    thumbURL: thumbURL || null,
                    gender: genders[i],
                    order: 0,
                };
                await dateCardTemplateService.create(template, isNewCategory && i === 0);
            }
            navigate('/date-card-designs');
        } finally {
            setSaving(false);
        }
    }

    const isOther = selectedCategory === OTHER_VALUE;
    const canSave =
        !!name.trim() &&
        !!file &&
        !!thumb &&
        (genderMen || genderWomen) &&
        (isOther ? !!newCategory.trim() : !!selectedCategory);

    return (
        <Box>
            <Typography variant={'h1'} sx={{mb: 4}}>Add Design</Typography>
            <Card sx={{p: 4, pointerEvents: saving ? 'none' : undefined, opacity: saving ? 0.6 : undefined}}>
                <FormControl fullWidth sx={{mb: 3}}>
                    <InputLabel id={'category-label'}>Category</InputLabel>
                    <Select
                        labelId={'category-label'}
                        label={'Category'}
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {existingCategories.map((cat) => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                        <MenuItem value={OTHER_VALUE}>Other...</MenuItem>
                    </Select>
                </FormControl>

                {isOther && (
                    <TextField
                        fullWidth
                        sx={{mb: 3}}
                        label={'New Category Name'}
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                    />
                )}

                <FormControl sx={{mb: 3}}>
                    <FormLabel>Gender</FormLabel>
                    <FormGroup row>
                        <FormControlLabel
                            control={<Checkbox checked={genderMen} onChange={(e) => setGenderMen(e.target.checked)}/>}
                            label={'Men'}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={genderWomen} onChange={(e) => setGenderWomen(e.target.checked)}/>}
                            label={'Women'}
                        />
                    </FormGroup>
                </FormControl>

                <TextField
                    fullWidth
                    sx={{mb: 3}}
                    label={'Title'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                {preview && (
                    <Box sx={{mb: 2}}>
                        <img src={preview} alt="" style={{maxHeight: '400px', maxWidth: '100%'}}/>
                        <Box sx={{display: 'flex', gap: 3, mt: 1}}>
                            {file && (
                                <Typography variant={'caption'} color={'text.secondary'}>
                                    Original: {(file.size / 1024).toFixed(0)} KB
                                </Typography>
                            )}
                            {thumbBlob && (
                                <Typography variant={'caption'} color={'text.secondary'}>
                                    thumbURL: {(thumbBlob.size / 1024).toFixed(0)} KB
                                </Typography>
                            )}
                            {thumb && (
                                <Typography variant={'caption'} color={'text.secondary'}>
                                    thumb: ~{(thumb.split(',')[1].length * 0.75 / 1024).toFixed(0)} KB
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                <Button variant={'outlined'} component={'label'} sx={{mb: 3}}>
                    Upload Image
                    <input hidden type="file" accept={'image/*'} onChange={(e) => {
                        if (e.target.files?.length) setFile(e.target.files[0]);
                    }}/>
                </Button>

                <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                        variant={'contained'}
                        disabled={!canSave || saving}
                        onClick={() => void save()}
                        sx={{minWidth: 90}}
                    >
                        {saving ? <CircularProgress size={22} color={'inherit'}/> : 'Save'}
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};
